/**
 * Audio cues played as a candidate's per-question timer approaches expiry.
 *
 * Three cues, deliberately distinguishable by pitch, rhythm, timbre, and duration:
 *
 *   - Warning beep (~70% elapsed): a single long 900 Hz sine tone, ~0.9s.
 *     "One clear wake-up beep."
 *
 *   - Urgent beep (~90% elapsed): four fast 1400 Hz sine pulses, ~0.8s total.
 *     "You are running out of time."
 *
 *   - Final beep (100%): six alternating 2200 / 1600 Hz sawtooth pulses,
 *     ~1.2s total, at maximum gain. Sawtooth timbre is noticeably harsher
 *     than the sine cues. "Time's up."
 *
 * Generated with Web Audio API — no audio assets shipped.
 */

let audioCtx = null;

const getContext = () => {
  if (audioCtx) return audioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioCtx = new Ctor();
    return audioCtx;
  } catch (e) {
    return null;
  }
};

// Play a single tone at `startAt` seconds from now.
const scheduleTone = (ctx, freq, startAt, durationSec, peakGain, type = 'sine') => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + startAt);

  // Quick attack + short decay envelope to avoid clicks but stay loud.
  const start = ctx.currentTime + startAt;
  const end = start + durationSec;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peakGain, start + 0.01);
  gain.gain.setValueAtTime(peakGain, end - 0.05);
  gain.gain.linearRampToValueAtTime(0, end);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(end + 0.02);
};

const ensureRunning = (ctx) => {
  if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
    ctx.resume().catch(() => {});
  }
};

/**
 * Warning cue: one long, loud 900 Hz sine tone (~0.9s).
 */
export const playWarningBeep = () => {
  const ctx = getContext();
  if (!ctx) return;
  try {
    ensureRunning(ctx);
    scheduleTone(ctx, 900, 0, 0.9, 0.9, 'sine');
  } catch (e) {
    // Silent fail — visuals still convey the remaining time.
  }
};

/**
 * Urgent cue: four rapid 1400 Hz sine pulses, 0.12s on / 0.08s off.
 */
export const playUrgentBeep = () => {
  const ctx = getContext();
  if (!ctx) return;
  try {
    ensureRunning(ctx);
    const pulseOn = 0.12;
    const pulseOff = 0.08;
    for (let i = 0; i < 4; i++) {
      const startAt = i * (pulseOn + pulseOff);
      scheduleTone(ctx, 1400, startAt, pulseOn, 0.95, 'sine');
    }
  } catch (e) {
    // Silent fail.
  }
};

/**
 * Final cue: six alternating 2200 / 1600 Hz sawtooth pulses, 0.15s on / 0.05s off.
 * Sawtooth wave is richer in harmonics — sounds noticeably harsher than the
 * preceding sine cues, which is the point: the candidate should hear it and
 * know time has run out, not just that it's close.
 */
export const playFinalBeep = () => {
  const ctx = getContext();
  if (!ctx) return;
  try {
    ensureRunning(ctx);
    const pulseOn = 0.15;
    const pulseOff = 0.05;
    const highFreq = 2200;
    const lowFreq = 1600;
    for (let i = 0; i < 6; i++) {
      const startAt = i * (pulseOn + pulseOff);
      const freq = i % 2 === 0 ? highFreq : lowFreq;
      scheduleTone(ctx, freq, startAt, pulseOn, 1.0, 'sawtooth');
    }
  } catch (e) {
    // Silent fail.
  }
};

/**
 * Schedule the 70% and 90% cues for `totalMs`. The final/100% cue is played
 * directly by the auto-stop handler so it's deterministic and never silenced
 * by a concurrent cancel(). If the current time has already passed a threshold
 * (e.g. reload edge case) that cue is skipped. Returns { cancel } — invoking
 * it clears the scheduled timeouts so they never fire after a manual submit
 * or component unmount.
 */
export const scheduleTimeoutBeeps = (totalMs) => {
  const warningAt = Math.floor(totalMs * 0.7);
  const urgentAt = Math.floor(totalMs * 0.9);

  const warningId = warningAt > 0 ? setTimeout(playWarningBeep, warningAt) : null;
  const urgentId = urgentAt > 0 ? setTimeout(playUrgentBeep, urgentAt) : null;

  return {
    cancel: () => {
      if (warningId) clearTimeout(warningId);
      if (urgentId) clearTimeout(urgentId);
    },
  };
};
